/**
 * 工具方法模块
 * @author  zhengchao
 * @Date 2017-05-09
 */
import { Cookies } from "@ok/blade";

const pbkdf2Param = {
  salt: 'okexisnumberone',
  iterations: 2000,
  keyLenBytes: 32,
};

export default {
  objToQueryString(obj) {
    if (!obj) {
      return '';
    }
    const str = [];
    for (const p in obj) {
      if (obj.hasOwnProperty(p)) {
        str.push(`${encodeURIComponent(p)}=${encodeURIComponent(obj[p])}`);
      }
    }
    return str.join('&');
  },
  /**
   * 格式化timestamp
   * @param  timestamp
   * @param  {string} format    格式字符串, 如 yyyy-MM-dd, yyyy-MM-dd hh:mm:ss
   * @return {string}           格式化后的字符串
   */
  formatTimestamp(timestamp, format) {
    const inputDate = new Date(timestamp);
    const date = {
      'M+': inputDate.getMonth() + 1,
      'd+': inputDate.getDate(),
      'h+': inputDate.getHours(),
      'm+': inputDate.getMinutes(),
      's+': inputDate.getSeconds(),
      'q+': Math.floor((inputDate.getMonth() + 3) / 3),
      'S+': inputDate.getMilliseconds(),
    };
    if (/(y+)/i.test(format)) {
      format = format.replace(
        RegExp.$1,
        inputDate
          .getFullYear()
          .toString()
          .substr(4 - RegExp.$1.length)
      );
    }
    for (const k in date) {
      if (new RegExp(`(${k})`).test(format)) {
        format = format.replace(
          RegExp.$1,
          RegExp.$1.length == 1
            ? date[k]
            : `00${date[k]}`.substr(`${date[k]}`.length)
        );
      }
    }
    return format;
  },

  /** *
   * 获取devId  登录注册时用到
   */
  getDeviceId() {
    if (window.localStorage) {
      const { ont } = window.utils;
      let devId = ont.getDeviceId();
      if (!devId) {
        devId = _uuid();
        localStorage.setItem('devId', devId);
      }
      return devId;
    }

    function _uuid() {
      let d = Date.now();
      if (
        typeof performance !== 'undefined' &&
        typeof performance.now === 'function'
      ) {
        d += performance.now(); // use high-precision timer if available
      }
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });
    }
  },

  /**
   * 获取url 参数
   * @param name
   * @returns {*}
   */
  getUrlParam(name) {
    const reg = new RegExp(`(^|&)${name}=([^&]*)(&|$)`);
    const r = window.location.search.substr(1).match(reg);
    if (r != null) {
      return unescape(r[2]);
    }
    return null;
  },

  // 该方法用来 避免 解析html  时带来的 xss攻击 危害
  escape(str) {
    return str
      .replace(/\<br\/\>/g, '\n')
      .replace(/[<>"]/g, (match) => {
        switch (match) {
          case '<':
            return '&lt;';
          case '>':
            return '&gt;';
          case '&':
            return '&amp;';
          case '"':
            return '&quot;';
        }
      })
      .replace(/\n/g, '<br/>');
  },

  deepCopy(obj) {
    try {
      // 终止条件，如果不是对象就放回该值
      if (typeof obj !== 'object' || obj === null) {
        return obj;
      }
      const newObj = obj.constructor === Array ? [] : {};
      Object.keys(obj).forEach((key) => {
        // 利用递归的方式
        newObj[key] = this.deepCopy(obj[key]);
      });
      return newObj;
    } catch (e) {
      console.log(e);
      return null;
    }
  },

  deepCopyIncludeFiles(obj) {
    try {
      // 终止条件，如果不是对象就放回该值
      if (typeof obj !== 'object' || obj === null) {
        return obj;
      }
      const newObj = obj.constructor === Array ? [] : {};
      Object.keys(obj).forEach((key) => {
        if (obj[key] instanceof File) {
          const clonedBlob = new Blob([obj[key]], { type: obj[key].type });
          // 创建一个新的 File 对象，使用 Blob 对象和文件名
          const clonedFile = new File([clonedBlob], obj[key].name, {
            type: obj[key].type,
          });
          newObj[key] = clonedFile;
        } else {
          // 利用递归的方式
          newObj[key] = this.deepCopyIncludeFiles(obj[key]);
        }
      });
      return newObj;
    } catch (e) {
      console.log(e);
      return null;
    }
  },

  /**
   * 浅拷贝
   * @param obj
   */
  shallowCopy(obj) {
    const newobj = {};
    if (obj) {
      const keys = Object.keys(obj);
      keys.forEach((item) => {
        newobj[item] = obj[item];
      });
    }
    return newobj;
  },

  /**
   * 浏览器判断
   * @returns {boolean}
   */
  lessThanIE11() {
    const UA = navigator.userAgent;
    const isIE = UA.indexOf('MSIE') > -1;
    const v = isIE ? /\d+/.exec(UA.split(';')[1]) : 11;
    return v < 11;
  },

  /**
   * 获取 慢hash加密 配置
   */
  getPbkdf2Param() {
    return pbkdf2Param;
  },

  /**
   * 判断一级域名及顶级域名与当前url是否相等
   * @param url
   */
  firstLevelDomainEqual(url) {
    const local = window.location.href;
    const urlHasDomain = url.includes('//');

    // 不存在域名则为同域名
    if (!urlHasDomain) {
      return true;
    }
    // 去掉http 去掉 path 去掉端口 按点切分
    const localDomainList = local
      .split('//')[1]
      .split('/')[0]
      .split(':')[0]
      .split('.');
    const urlDomainList = url
      .split('//')[1]
      .split('/')[0]
      .split(':')[0]
      .split('.');

    // 顶级域名相等
    const topDomainEqual =
      localDomainList[localDomainList.length - 1] ===
      urlDomainList[urlDomainList.length - 1];
    // 一级域名相等
    const firstDomainEqual =
      localDomainList[localDomainList.length - 2] ===
      urlDomainList[urlDomainList.length - 2];

    return topDomainEqual && firstDomainEqual;
  },

  debounce(fn, delay) {
    let timer = null;
    return function () {
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(fn, delay);
    };
  },
};
